import React, { useEffect, useState } from 'react';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Activity,
  TrendingUp,
  UserPlus,
  ListOrdered,
} from 'lucide-react';
import { getAnalytics } from '../api/services';
import StatCard from '../components/StatCard';
import { formatDate } from '../utils/admin';

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const data = await getAnalytics();
        if (data.success) {
          setAnalytics(data.analytics);
        }
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        {error}
      </div>
    );
  }

  const summary = analytics?.summary;
  const dailySignups = analytics?.signups?.daily || [];
  const weeklySignups = analytics?.signups?.weekly || [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
      
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Users" 
          value={summary?.totalUsers || 0} 
          icon={Users} 
          color="blue" 
        />
        <StatCard 
          title="Pending Review" 
          value={summary?.pendingReview || 0} 
          icon={Clock} 
          color="yellow" 
        />
        <StatCard 
          title="Active Users (7d)" 
          value={summary?.activeUsers7d || 0} 
          icon={Activity} 
          color="green" 
        />
        <StatCard 
          title="Recent Signups (24h)" 
          value={summary?.recentSignups24h || 0} 
          icon={UserPlus} 
          color="gray" 
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard 
          title="Accepted Users" 
          value={summary?.accepted || 0} 
          icon={UserCheck} 
          color="green" 
        />
        <StatCard 
          title="Rejected Users" 
          value={summary?.rejected || 0} 
          icon={UserX} 
          color="red" 
        />
        <StatCard
          title="Waitlisted Users"
          value={summary?.waitlisted || 0}
          icon={ListOrdered}
          color="yellow"
        />
        <StatCard 
          title="Acceptance Rate" 
          value={summary?.acceptanceRate || '0%'} 
          icon={TrendingUp} 
          color="gray" 
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Daily Signups</h2>
          {dailySignups.length === 0 ? (
            <p className="text-sm text-gray-500">No signup data available.</p>
          ) : (
            <div className="space-y-3">
              {dailySignups.slice(-7).reverse().map((entry) => (
                <div key={entry._id} className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                  <span className="text-sm text-gray-600">{formatDate(entry._id)}</span>
                  <span className="font-semibold text-gray-900">{entry.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Weekly Signups</h2>
          {weeklySignups.length === 0 ? (
            <p className="text-sm text-gray-500">No weekly trend data available.</p>
          ) : (
            <div className="space-y-3">
              {weeklySignups.slice(-6).reverse().map((entry) => (
                <div key={entry._id} className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                  <span className="text-sm text-gray-600">Week {entry._id}</span>
                  <span className="font-semibold text-gray-900">{entry.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
